import React, { useCallback, useMemo } from 'react';
import { BaseAction } from '../../models';
import { ButtonAction } from '../button-action';
import { useOpen } from 'AurionCR/components/hooks';
import { apiNotebooks } from 'services/notebook';
import { getNotebookFormComponent } from 'components/notebook-form-generator/helpers';
import { apiTodoTasks } from 'services/todo-tasks';
import { UserPatientProfileSession } from 'services/user-patient-profile-session';

const useFetchNotebook = apiNotebooks.useLazyGetNotebookEndOfTreatmentSummaryQuery;
const usePatchTodoTask = apiTodoTasks.usePatchTodoTaskMutation;

interface Props extends BaseAction {
  userPatientProfileID: string;
}
export const ActionEndOfTreatment: React.FC<React.PropsWithChildren<Props>> = ({
  taskID,
  onDone,
  actionKey,
  isLoading,
  userPatientProfileID,
}) => {
  const [fetchNotebook, resultFetchNotebook] = useFetchNotebook();
  const [patchTodoTask, resultPatchTodoTask] = usePatchTodoTask();
  const notebook = resultFetchNotebook.data ?? null;
  const notebookID = notebook?.id ?? null;

  const { isOpen, handleOpen, handleClose } = useOpen();

  const handleClick = useCallback(async () => {
    await fetchNotebook(undefined);
    handleOpen();
  }, [fetchNotebook, handleOpen]);

  const disabled = resultFetchNotebook.isFetching || isLoading || resultPatchTodoTask.isLoading;
  const FormGenerator = useMemo(() => {
    return notebook ? getNotebookFormComponent(notebook) : null;
  }, [notebook]);

  const handleDone = useCallback(
    async (session: Pick<UserPatientProfileSession, 'id'>) => {
      handleClose();

      await patchTodoTask({
        id: taskID,
        userPatientProfileSessionID: session.id,
        done: true,
      });

      onDone();
    },
    [handleClose, onDone, taskID, patchTodoTask],
  );

  return (
    <>
      <ButtonAction actionKey={actionKey} onClick={handleClick} disabled={disabled} />
      {isOpen && FormGenerator && notebookID && (
        <FormGenerator
          notebookID={notebookID}
          userPatientProfileID={userPatientProfileID}
          onClose={handleClose}
          onDone={handleDone}
          meetingFromDateTime={undefined}
          meetingToDateTime={undefined}
        />
      )}
    </>
  );
};
